import { PayloadAction } from '@reduxjs/toolkit';
import { isEqual, omit, uniqBy } from 'lodash-es';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { SYNC_WITH_STORAGE_ACTION } from 'state/global-state/global-actions';
import { createAppSlice } from 'state/global-state/utils';
import { IncentiveEvaluationMap } from 'state/loyalty/hooks/types';
import {
  AppPersonalizedOffer,
  AppPersonalizedOfferGroup,
  EngineNonPersonalizedOfferListItem,
  EngineOfferListItem,
  LoyaltyAppliedOffer,
  LoyaltyOffer,
  isSanityOfferListItem,
} from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';

import { userSlice } from '../user/user.slice';
import { LoyaltyUser } from '../user/user.types';

import { IOffersState } from './offers.types';
import {
  getAppliedOffersFromStorage,
  getSelectedOfferFromStorage,
  removeSelectedOfferInStorage,
  updateAppliedOffersInStorage,
  updateSelectedOfferInStorage,
} from './offers.utils';

export const initialState: IOffersState = {
  appliedOffers: [],
  cmsOffers: [],
  incentivesIds: [],
  guestEngineOfferListItems: [],
  offersFeedbackMap: {},
  offersLoading: false,
  offerRedemptionAvailableAfter: null,
  appPersonalizedOffers: [],
  appPersonalizedOfferGroups: [],
  selectedOffer: null,
  engineOfferListItem: [],
  shouldRefetchOffers: true, // needed for initial load
  loyaltyUserId: undefined,
};

export const offersSlice = createAppSlice({
  name: 'offers',
  initialState,
  reducers: {
    applyOffer: (state, { payload: newOffer }: PayloadAction<LoyaltyAppliedOffer>) => {
      const isStackableOffer = newOffer.isStackable;
      const updatedOffers = state.appliedOffers
        .filter((appliedOffer: LoyaltyAppliedOffer) => {
          if (isStackableOffer) {
            // Ensures we are not adding the same offer
            return appliedOffer.id !== newOffer.id;
          }
          // filters out non-stackable offers
          return appliedOffer.isStackable;
        })
        .concat(newOffer);
      updateAppliedOffersInStorage(updatedOffers);
      state.appliedOffers = updatedOffers;
    },
    unshiftAppPersonalizedOffer: (state, { payload }: PayloadAction<AppPersonalizedOffer>) => {
      state.appPersonalizedOffers = [payload, ...state.appPersonalizedOffers];
    },
    resetAppliedOffers: state => {
      updateAppliedOffersInStorage([]);
      state.appliedOffers = [];
    },
    resetAppliedDiscountOffers: state => {
      const discountCmsOffersIds = new Set<string>();
      state.cmsOffers.forEach(cmsOffer => {
        if (
          isSanityOfferListItem(cmsOffer) &&
          cmsOffer.loyaltyEngineId &&
          isDiscountLoyaltyOffer(cmsOffer)
        ) {
          discountCmsOffersIds.add(cmsOffer.loyaltyEngineId);
        }
      });

      const updatedOffers = state.appliedOffers.filter(
        appliedOffer => !discountCmsOffersIds.has(appliedOffer.id || '')
      );
      updateAppliedOffersInStorage(updatedOffers);
      state.appliedOffers = updatedOffers;

      // Also clear selectedOffer if it's a discount offer being removed
      if (
        state.selectedOffer?.loyaltyEngineId &&
        discountCmsOffersIds.has(state.selectedOffer.loyaltyEngineId)
      ) {
        removeSelectedOfferInStorage();
        state.selectedOffer = null;
      }
    },
    resetOfferFeedbackMap: state => {
      state.offersFeedbackMap = {};
    },
    setAppliedOffers: (state, { payload }: PayloadAction<LoyaltyAppliedOffer[]>) => {
      updateAppliedOffersInStorage(payload);
      state.appliedOffers = payload;
    },
    setCmsOffers: (state, { payload }: PayloadAction<IOffersState['cmsOffers']>) => {
      state.cmsOffers = uniqBy([...payload, ...state.cmsOffers], '_id');
    },
    setIncentivesIds: (state, { payload }: PayloadAction<string[]>) => {
      state.incentivesIds = payload;
    },
    setGuestEngineOfferListItems: (state, { payload }: PayloadAction<EngineOfferListItem[]>) => {
      state.guestEngineOfferListItems = payload;
    },
    setOffersFeedbackMap: (state, { payload }: PayloadAction<IncentiveEvaluationMap>) => {
      state.offersFeedbackMap = payload;
    },
    setOffersLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.offersLoading = payload;
    },
    // TODO: remove this function and get the value from the user
    setOfferRedemptionAvailableAfter: (state, { payload }: PayloadAction<string | null>) => {
      state.offerRedemptionAvailableAfter = payload;
    },
    setAppPersonalizedOffers: (state, { payload }: PayloadAction<AppPersonalizedOffer[]>) => {
      state.appPersonalizedOffers = payload;
    },
    setAppPersonalizedOfferGroups: (
      state,
      { payload }: PayloadAction<AppPersonalizedOfferGroup[]>
    ) => {
      state.appPersonalizedOfferGroups = payload;
    },
    setSelectedOffer: (state, { payload }: PayloadAction<LoyaltyOffer | null>) => {
      updateSelectedOfferInStorage(payload);
      state.selectedOffer = payload;
    },
    setEngineOfferListItems: (
      state,
      { payload }: PayloadAction<EngineNonPersonalizedOfferListItem[]>
    ) => {
      state.engineOfferListItem = payload;
    },
    removeAppliedOffer: (state, { payload: appliedOffer }: PayloadAction<LoyaltyAppliedOffer>) => {
      const updatedOffers = state.appliedOffers.filter(offer => {
        return offer.cartId === appliedOffer.cartId;
      });

      updateAppliedOffersInStorage(updatedOffers);

      state.appliedOffers = updatedOffers;
    },
    removeAppliedOfferByCartEntry: (
      state,
      { payload: cartEntry }: PayloadAction<ILegacyCartEntry>
    ) => {
      const updatedOffers = state.appliedOffers.filter(offer => {
        if (
          cartEntry.cartId === offer.cartId &&
          offer.id === state.selectedOffer?.loyaltyEngineId
        ) {
          removeSelectedOfferInStorage();
          state.selectedOffer = null;
        }
        if (offer?.id && state.offersFeedbackMap[offer.id]) {
          state.offersFeedbackMap = omit(state.offersFeedbackMap, offer.id);
        }
        // If cart entry has children, cartIds could match
        return offer?.cartId !== cartEntry?.cartId;
      });

      updateAppliedOffersInStorage(updatedOffers);

      state.appliedOffers = updatedOffers;
    },
    setShouldRefetchOffers: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldRefetchOffers = payload;
    },
  },
  extraReducers: {
    [SYNC_WITH_STORAGE_ACTION]: (state: IOffersState) => {
      const offersFromStorage = getAppliedOffersFromStorage();
      if (!isEqual(state.appliedOffers, offersFromStorage)) {
        state.appliedOffers = offersFromStorage;
      }
      state.selectedOffer = getSelectedOfferFromStorage();
    },
    [userSlice.actions.setUser.type]: (
      state: IOffersState,
      { payload: loyaltyUser }: PayloadAction<LoyaltyUser | null>
    ) => {
      // If loyalty user is null, reset user offers
      // This handles the logout case
      if (!loyaltyUser) {
        state.appPersonalizedOffers = [];
        state.appPersonalizedOfferGroups = [];
        state.engineOfferListItem = [];
        state.offerRedemptionAvailableAfter = null;
      }

      // Update offers only when loyaltyUser changes
      if (state.loyaltyUserId !== loyaltyUser?.id) {
        state.shouldRefetchOffers = true;
      }

      // Cache the loyalty user id
      state.loyaltyUserId = loyaltyUser?.id;
    },
  },
});
